import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Injector, Input, Output, ViewChild } from '@angular/core';
import { LocalizationService, LocalizationModule } from '@abp/ng.core';
import {
  CellValueChangedEvent,
  ColDef,
  EditableCallbackParams,
  GetContextMenuItemsParams,
  MenuItemDef,
} from '@ag-grid-community/core';
import { Observable, of } from 'rxjs';
import {
  BaseDocTemplateLineDto,
  DocLineRole,
  DocTemplateLineUpdateDto,
  DocTemplateService,
  ThirdPartyDocDto,
} from '@digiwall/goliath-documents/proxy';
import {
  BaseThirdPartyDocLineEditor,
  BaseThirdPartyDocServiceForLineEditor,
  LinesEditorComponent,
} from '../../../../components';
import { ObserverQueue } from '@digiwall/core';

@Component({
  selector: 'document-template-lines-editor',
  templateUrl: './doc-template-lines-editor.component.html',
  imports: [CommonModule, LinesEditorComponent, LocalizationModule],
  providers: [ObserverQueue],
})
export class DocTemplateLinesEditorComponent extends BaseThirdPartyDocLineEditor<ThirdPartyDocDto> {
  @ViewChild('lineGrid') lineGrid: LinesEditorComponent;

  @Input() set docTemplate(value) {
    this._document = value;
    this.setLanguageCode();
  }

  @Output() linesChanged = new EventEmitter<void>();

  protected articleLineRole: DocLineRole = DocLineRole.CustomerQuoteArticle;
  protected textLineRole: DocLineRole = DocLineRole.DocText;
  protected chapterLineRole: DocLineRole = DocLineRole.DocChapter;

  public columnDefs: Array<ColDef> = [];

  protected override deleteActionEnabled = true;

  constructor(
    localizationService: LocalizationService,
    docTemplateService: DocTemplateService,
    injector: Injector,
    updateQueue: ObserverQueue,
  ) {
    const extendedDocTemplateService: BaseThirdPartyDocServiceForLineEditor = Object.assign(
      {},
      docTemplateService,
      {
        setLineIsHidden: () => null,
        setLineVatCode: () => null,
        selectArticleByEAN: () => null,
        checkArticleAndLineCompatibility: () => {
          return of({ isCompatible: true });
        },
        setLineArticleSerial: () => null,
        setLineArticleLot: () => null,
        setLineArticleRangeValue2: () => null,
        setLineArticleRangeValue1: () => null,
        setLineStockageLocation: () => null,
        setLineLength: () => null,
        setLineWidth: () => null,
        setLineThickness: () => null,
        setLineHasSubTotal: () => null,
        setLineStockQuantity: () => null,
        addArticleLines: () => null,
      },
    );

    //TODO ATTENTION #25054
    super(localizationService, extendedDocTemplateService, injector, updateQueue);
    this.initColDefs();
  }

  private initColDefs(): void {
    this.columnDefs = super.colDefs();
    this.columnDefs.push(
      ...([
        this.colDef_Quantity,
        {
          field: 'isOptionnal',
          headerName: this.localizationService.instant('Doc::IsOptionnal'),
          headerTooltip: this.localizationService.instant('Doc::IsOptionnal:Tooltip'),
          cellRenderer: 'agCheckboxCellRenderer',
          cellEditor: 'agCheckboxCellEditor',
          sortable: false,
          editable: params => this.isCellEditable(params),
          suppressMovable: true,
          pinned: 'right',
          lockPinned: true,
        },
      ] as Array<ColDef>),
    );
  }

  public override isCellEditable(params: EditableCallbackParams<any, any>) {
    let isEditable = true;

    if (params.data.lineRole == this.articleLineRole && params.colDef.field != 'code') {
      isEditable = params.data.articleId != null;
    }

    return isEditable && super.isCellEditable(params);
  }

  protected getContextMenuItems = (params: GetContextMenuItemsParams): (string | MenuItemDef)[] => {
    return this.contextMenuItems(params);
  };

  protected onCellValueChanged = (
    event: CellValueChangedEvent<any, any>,
  ): void | PromiseLike<void> => {
    this.linesChanged.emit();
    let obs: Observable<DocTemplateLineUpdateDto> = this.getCellValueChangedObservable(event);

    this._updateQueue
      .enqueue(() => obs)
      .subscribe({
        next: response => {
          this.updateTemplateLines(response);
        },
        error: _ => {
          let oldData: BaseDocTemplateLineDto = event.data;
          oldData[event.colDef.field] = event.oldValue;
          this.lineGrid.grid.grid.api.applyTransaction({
            update: [oldData],
          });
        },
      });
  };

  protected updateTemplateLines = (docTemplateUpdated: DocTemplateLineUpdateDto) => {
    if (docTemplateUpdated == null) return;

    const transactionData = this.createGridTransaction(docTemplateUpdated.updatedLines);

    this.lineGrid.grid.grid.api.applyTransaction(transactionData);

    this.lineGrid.grid.grid.api.redrawRows();
  };

  isChapter = line => line.lineRole == DocLineRole.DocChapter;
}
